import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Image as ImageIcon,
  FileText,
  File,
  Building2,
  MapPin,
  Sparkles,
  Layers,
  Home,
  Ruler,
  Info,
  Map as MapIcon,
  ChevronRight,
} from 'lucide-react';
import { OutlineButton } from '../../components/Buttons';
import axiosInstance from '../../services/api/axios';
import { fetchPropertyConfigurations, fetchProjectPhases } from '../../services/projects';
import { buildPhaseDataItemFromProjectPhase } from '../../utils/buildInteriorPhasePayload';
import { toast } from 'react-toastify';
import { useUser } from '../../context/UserContext';
import { getMediaUrl, normalizePropertyConfigMediaForUI } from '../../utils/media';
import { getCachedConfigMediaArray, setCachedConfigMediaArray } from '../../utils/configMediaSessionCache';
import ImageCarousel from '../../components/ImageCarousel';

// Tabs available on this page. Hash-driven so the URL reflects the active section.
// Designs tab is intentionally NOT present on this view — it lives on a separate
// ID page now, and the BV user view never needs to show it.
const TAB_IDS = ['overview', 'properties', 'images', 'floorplans'];
const parseTabFromHash = (hash) => {
  if (!hash) return 'overview';
  const cleaned = hash.replace(/^#/, '').toLowerCase();
  return TAB_IDS.includes(cleaned) ? cleaned : 'overview';
};

/**
 * BV user config details: configuration fields, gallery carousel, and detailed floor plans.
 *
 * Visual language mirrors the InteriorDesigner ConfigDetails page — slate hero band,
 * pill tab nav, section cards, stat tiles. Behavior (fetches, caches, navigation) is
 * preserved as-is from the previous implementation.
 */
const BV_ConfigDetails = () => {
  const { projectId, configId } = useParams();
  const navigate = useNavigate();
  const navLocation = useLocation();
  const { userInfo } = useUser();

  // Get config and project data from navigation state if available
  const passedConfigData = navLocation.state?.configData;
  const passedProjectData = navLocation.state?.projectData;
  const passedPhaseId = navLocation.state?.phaseId;
  const [config, setConfig] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryMedia, setGalleryMedia] = useState([]);
  const [detailedFloorPlans, setDetailedFloorPlans] = useState([]);
  const [propertyCount, setPropertyCount] = useState(0);
  const [phaseId, setPhaseId] = useState(null);
  const [buildings, setBuildings] = useState(() => navLocation.state?.buildings ?? []);
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== 'undefined' ? parseTabFromHash(window.location.hash) : 'overview'
  );

  // Keep activeTab in sync with the URL hash (back/forward, deep links).
  useEffect(() => {
    setActiveTab(parseTabFromHash(navLocation.hash));
  }, [navLocation.hash]);

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    if (typeof window !== 'undefined') {
      navigate(`${navLocation.pathname}#${tabId}`, { replace: true });
    }
  };

  useEffect(() => {
    const fetchConfigDetails = async () => {
      try {
        setLoading(true);

        // Use passed config data if available, otherwise fetch
        let configData = passedConfigData;
        let projectData = passedProjectData;
        let extractedPhaseId = passedPhaseId;

        if (!configData) {
          const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
          const params = { projectId };
          if (customerId) {
            params.customerId = customerId;
          }
          if (passedPhaseId) {
            params.projectPhaseId = passedPhaseId;
          }
          const configResponse = await fetchPropertyConfigurations(params);

          let configsArray = [];
          if (configResponse?.items && Array.isArray(configResponse.items)) {
            configsArray = configResponse.items;
          } else if (Array.isArray(configResponse)) {
            configsArray = configResponse;
          }

          configData = configsArray.find(
            (c) => (c.propertyConfigId || c.id) == configId
          );

          if (!configData) {
            toast.error('Configuration not found');
            navigate('/bv-user/phases');
            return;
          }

          // Extract phaseId from config if available (check both direct and nested)
          extractedPhaseId = configData.projectPhaseId || configData.projectPhase?.projectPhaseId;

          // Extract project data from config response (nested in projectPhase.project)
          if (configData.projectPhase?.project) {
            projectData = configData.projectPhase.project;
          }
        }

        // Fallback: If project not found and not passed, fetch separately
        if (!projectData) {
          console.warn('Project not found, fetching separately...');
          try {
            const projectResponse = await axiosInstance.get(`/projects`, {
              params: {
                projectId,
                customerId: null,
              },
            });

            if (projectResponse?.items && Array.isArray(projectResponse.items) && projectResponse.items.length > 0) {
              projectData = projectResponse.items[0];
            } else if (Array.isArray(projectResponse) && projectResponse.length > 0) {
              projectData = projectResponse[0];
            }
          } catch (error) {
            console.error('Error fetching project details:', error);
          }
        }

        setConfig(configData);
        setProject(projectData);

        if (extractedPhaseId) {
          setPhaseId(extractedPhaseId);
        }

        // Buildings: navigation state, else /projects/buildings, else phase row
        let buildingsList = Array.isArray(navLocation.state?.buildings)
          ? navLocation.state.buildings
          : [];
        if (!buildingsList.length && extractedPhaseId && projectId) {
          try {
            const bParams = { projectPhaseId: extractedPhaseId };
            const custId = userInfo?.loginIdentifierDetails?.customer?.customerId;
            if (custId) bParams.customerId = custId;
            const bResp = await axiosInstance.get('/projects/buildings', { params: bParams });
            let bArr = [];
            if (bResp?.items && Array.isArray(bResp.items)) bArr = bResp.items;
            else if (Array.isArray(bResp)) bArr = bResp;
            if (bArr.length) buildingsList = bArr;
          } catch (bErr) {
            console.error('Error fetching buildings:', bErr);
          }
        }
        if (!buildingsList.length && extractedPhaseId && projectId) {
          try {
            const phaseResponse = await fetchProjectPhases({
              projectPhaseId: extractedPhaseId,
              projectId,
            });
            let phasesArray = [];
            if (phaseResponse?.items && Array.isArray(phaseResponse.items)) {
              phasesArray = phaseResponse.items;
            } else if (Array.isArray(phaseResponse)) {
              phasesArray = phaseResponse;
            }
            const pid = Number(extractedPhaseId);
            const phaseRow =
              phasesArray.find(
                (p) =>
                  Number(p.projectPhaseId) === pid ||
                  Number(p.id) === pid ||
                  Number(p.projectPhase?.projectPhaseId) === pid
              ) || phasesArray[0];
            if (phaseRow) {
              const phaseItem = buildPhaseDataItemFromProjectPhase(phaseRow, projectData);
              buildingsList = phaseItem.buildings || [];
            }
          } catch (phErr) {
            console.error('Error loading buildings from phase:', phErr);
          }
        }
        setBuildings(buildingsList);

        // Config media: navigation state → session cache (from phase page) → network
        const cfgId = configData.propertyConfigId || configData.id;
        const fromNav = navLocation.state?.prefetchedConfigMedia;
        const fromSession = getCachedConfigMediaArray(cfgId);
        const prefetched =
          fromNav !== undefined && fromNav !== null ? fromNav : fromSession;

        if (prefetched !== undefined && prefetched !== null) {
          const { gallery, detailedFloorPlans: dfp } =
            normalizePropertyConfigMediaForUI(prefetched);
          setGalleryMedia(gallery);
          setDetailedFloorPlans(dfp);
        } else {
          try {
            const imagesResponse = await axiosInstance.get('/property/config/media', {
              params: {
                property_config_id: cfgId,
              },
            });

            setCachedConfigMediaArray(cfgId, imagesResponse);

            const { gallery, detailedFloorPlans: dfp } =
              normalizePropertyConfigMediaForUI(imagesResponse);
            setGalleryMedia(gallery);
            setDetailedFloorPlans(dfp);
          } catch (error) {
            console.error('Error fetching config images:', error);
            setGalleryMedia([]);
            setDetailedFloorPlans([]);
          }
        }

        // Get property count from config data if available (propertiesId array), otherwise fetch
        const configIdForCount = configData.propertyConfigId || configData.id;
        if (configData.propertiesId && Array.isArray(configData.propertiesId)) {
          // Use propertiesId array length from config data
          setPropertyCount(configData.propertiesId.length);
        } else {
          // Fallback: Fetch property count if not available in config data
          try {
            const propertiesResponse = await axiosInstance.get('/property', {
              params: {
                projectId: projectId,
                propertyConfigId: configIdForCount,
              },
            });

            let propertiesArray = [];
            if (propertiesResponse?.items && Array.isArray(propertiesResponse.items)) {
              propertiesArray = propertiesResponse.items;
            } else if (Array.isArray(propertiesResponse)) {
              propertiesArray = propertiesResponse;
            }

            setPropertyCount(propertiesArray.length);
          } catch (error) {
            console.error('Error fetching property count:', error);
            setPropertyCount(0);
          }
        }


      } catch (error) {
        console.error('Error fetching config details:', error);
        toast.error('Failed to load configuration details');
        navigate('/bv-user/phases');
      } finally {
        setLoading(false);
      }
    };

    if (projectId && configId) {
      fetchConfigDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, configId]); // Only depend on projectId and configId, not passed data to avoid refetching

  const handleBackClick = () => {
    const currentPhaseId = phaseId || config?.projectPhaseId || config?.projectPhase?.projectPhaseId;

    if (currentPhaseId) {
      navigate(`/bv-user/phases/${currentPhaseId}`);
    } else {
      navigate('/bv-user/phases');
    }
  };

  // Slate-language skeleton loader.
  const SkeletonLoader = () => (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
      <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
      <div className="h-12 w-72 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-48 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!config || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Building2 className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">Configuration not found</p>
          <p className="mt-1 text-sm text-slate-500">
            The configuration you&rsquo;re looking for is unavailable.
          </p>
          <OutlineButton onClick={handleBackClick} className="mt-4">
            Back to Phases
          </OutlineButton>
        </div>
      </div>
    );
  }

  const configName = config.configName || config.propertyConfig?.configName || 'Unnamed Config';
  const cityState = project.address
    ? `${project.address.city || ''}, ${project.address.state || ''}`.replace(/^,\s*|,\s*$/g, '')
    : 'Location not available';
  const fullAddress = (() => {
    if (!project.address) return cityState;
    const a = project.address;
    const parts = [
      [a.buildingNo, a.streetName1].filter(Boolean).join(' '),
      a.streetName2,
      a.district,
      a.city,
      a.state,
      a.pinCode,
    ].filter((s) => s && String(s).trim() !== '');
    return parts.length ? parts.join(', ') : cityState;
  })();

  // Format area helpers — preserve the same fallback chain as the previous file.
  const formatArea = (sqft, sqmt, fallbackField) => {
    if (typeof sqft === 'number') return `${sqft.toFixed(2)} sq.ft`;
    if (typeof sqmt === 'number') return `${sqmt.toFixed(2)} sq.mt`;
    if (fallbackField !== undefined && fallbackField !== null && fallbackField !== '')
      return `${fallbackField} sq.ft`;
    return 'N/A';
  };

  const carpetAreaText = formatArea(config.carpetAreaSqft, config.carpetAreaSqmt, config.carpetArea);
  const superBuiltupText = formatArea(config.superBuiltupAreaSqft, config.superBuiltupAreaSqmt);
  const balconyText = formatArea(config.balconyAreaSqft, config.balconyAreaSqmt);
  const dryBalconyText = formatArea(config.dryBalconyAreaSqft, config.dryBalconyAreaSqmt);

  const totalMediaFiles = galleryMedia.length + detailedFloorPlans.length;

  // Stat tiles shown in Overview.
  const statTiles = [
    {
      label: 'Total properties',
      value: propertyCount,
      icon: Home,
    },
    {
      label: 'Images',
      value: galleryMedia.length,
      icon: ImageIcon,
    },
    {
      label: 'Floor plans',
      value: detailedFloorPlans.length,
      icon: MapIcon,
    },
    {
      label: 'Size',
      value: config.sizeBhk ? `${config.sizeBhk} BHK` : '—',
      icon: Layers,
    },
  ];

  // Tab metadata — drives the pill nav.
  const TABS = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'properties', label: 'Properties', icon: Home, count: propertyCount },
    { id: 'images', label: 'Images', icon: ImageIcon, count: galleryMedia.length },
    { id: 'floorplans', label: 'Floor plans', icon: MapIcon, count: detailedFloorPlans.length },
  ];

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      {/* Back link above the hero */}
      <button
        type="button"
        onClick={handleBackClick}
        className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-flamingo-600"
      >
        <ArrowLeft size={14} />
        Back to phase
      </button>

      {/* Hero band — slate gradient with radial overlay */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-slate-800/30 to-transparent" />

        <div className="relative px-6 py-7 sm:px-8 sm:py-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
            <Sparkles size={12} />
            Buildvizer User &middot; Configuration
          </div>

          <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
            {configName}
          </h1>

          {(project.projectName || project.developer) && (
            <p className="mt-1 text-sm text-white/80">
              {project.projectName || project.developer}
              {project.projectName && project.developer ? (
                <span className="text-white/60"> &middot; {project.developer}</span>
              ) : null}
            </p>
          )}

          <div className="mt-2 flex items-start gap-1.5 text-xs text-white/70">
            <MapPin size={13} className="mt-0.5 shrink-0" />
            <span className="line-clamp-2">{fullAddress}</span>
          </div>

          {/* Stat pills row */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
              <Home size={12} />
              {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
              <ImageIcon size={12} />
              {totalMediaFiles} {totalMediaFiles === 1 ? 'media file' : 'media files'}
            </span>
            {config.propertyType && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                <Building2 size={12} />
                {config.propertyType}
              </span>
            )}
            {config.sizeBhk && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
                <Layers size={12} />
                {config.sizeBhk} BHK
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Pill tab nav */}
      <div className="inline-flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
        {TABS.map(({ id, label, icon: Icon, count }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => selectTab(id)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? 'bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              <Icon size={14} />
              {label}
              {typeof count === 'number' && count > 0 && (
                <span
                  className={`tabular-nums rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                    active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stat tiles */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-900">At a glance</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Quick snapshot of this configuration.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {statTiles.map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/40 p-4">
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    <Icon size={13} className="text-slate-400" />
                    {label}
                  </div>
                  <div className="mt-1.5 text-2xl font-semibold text-slate-900 tabular-nums">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About + key facts */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:col-span-2">
              <h2 className="text-lg font-semibold text-slate-900">About this configuration</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {project.developer ? `${project.developer} · ` : ''}
                {configName}
              </p>

              {config.description?.trim() ? (
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  {config.description.trim()}
                </p>
              ) : (
                <p className="mt-4 text-sm italic text-slate-400">No description provided.</p>
              )}

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 border-t border-slate-100 pt-5 text-sm sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Configuration name
                  </p>
                  <p className="mt-1 font-medium text-slate-900">
                    {config.configName || configName || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Property type
                  </p>
                  <p className="mt-1 font-medium text-slate-900">
                    {config.propertyType || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Size (BHK)
                  </p>
                  <p className="mt-1 font-medium text-slate-900">
                    {config.sizeBhk ? `${config.sizeBhk} BHK` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Base price
                  </p>
                  <p className="mt-1 font-semibold text-flamingo-600 tabular-nums">
                    {config.basePrice ? `₹${Number(config.basePrice).toLocaleString('en-IN')}` : '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Area breakdown */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-lg font-semibold text-slate-900">Area breakdown</h2>
              <p className="mt-0.5 text-sm text-slate-500">Measured area for this configuration.</p>

              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ruler size={13} className="text-slate-400" />
                    Carpet area
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{carpetAreaText}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ruler size={13} className="text-slate-400" />
                    Super built-up
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{superBuiltupText}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ruler size={13} className="text-slate-400" />
                    Balcony
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{balconyText}</span>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/40 px-3 py-2.5">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Ruler size={13} className="text-slate-400" />
                    Dry balcony
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{dryBalconyText}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Buildings */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900">Buildings</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {buildings.length === 0
                ? 'No buildings linked to this phase.'
                : `${buildings.length} ${buildings.length === 1 ? 'building' : 'buildings'} in this phase`}
            </p>

            {buildings.length === 0 ? (
              <div className="mt-5 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-6 text-center">
                <div className="mx-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                  <Building2 className="text-slate-400" size={18} />
                </div>
                <p className="mt-2 text-sm text-slate-500">None listed for this phase.</p>
              </div>
            ) : (
              <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {buildings.map((building) => {
                  const buildingId = building.buildingId || building.id;
                  const buildingName =
                    building.buildingName || building.name || 'Unnamed building';
                  const floors = building.numberOfFloors ?? building.floors;
                  const floorText =
                    floors != null && floors !== ''
                      ? `${floors} ${Number(floors) === 1 ? 'floor' : 'floors'}`
                      : null;
                  return (
                    <li
                      key={buildingId ?? buildingName}
                      className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/40 p-3"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 ring-1 ring-slate-200">
                        <Building2 size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {buildingName}
                        </p>
                        {floorText && (
                          <p className="text-xs text-slate-500">{floorText}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {activeTab === 'properties' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Properties</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {propertyCount === 0
                  ? 'No properties linked to this configuration yet.'
                  : `${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'} in this configuration`}
              </p>
            </div>
            {propertyCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <Home size={12} />
                {propertyCount}
              </span>
            )}
          </div>

          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Home className="text-slate-400" size={22} />
            </div>
            <p className="mt-3 text-base font-medium text-slate-800">
              {propertyCount === 0 ? 'No property details available' : `${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'} linked`}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {propertyCount === 0
                ? 'Properties will appear here once they are added to this configuration.'
                : 'Detailed property listings are managed on the phase page.'}
            </p>
            <OutlineButton onClick={handleBackClick} className="!mt-4 !w-auto !px-4 !py-2 text-sm">
              <span className="inline-flex items-center gap-1.5">
                Back to phase
                <ChevronRight size={14} />
              </span>
            </OutlineButton>
          </div>
        </div>
      )}

      {activeTab === 'images' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Images</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {galleryMedia.length === 0
                  ? 'No images uploaded for this configuration yet.'
                  : `${galleryMedia.length} ${galleryMedia.length === 1 ? 'image' : 'images'} available`}
              </p>
            </div>
            {galleryMedia.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <ImageIcon size={12} />
                {galleryMedia.length}
              </span>
            )}
          </div>

          {galleryMedia.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <ImageIcon className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No images available</p>
              <p className="mt-1 text-sm text-slate-500">
                Photos and renders will appear here once uploaded.
              </p>
            </div>
          ) : (
            <div className="rounded-xl overflow-hidden border border-slate-100">
              <ImageCarousel images={galleryMedia} />
            </div>
          )}
        </div>
      )}

      {activeTab === 'floorplans' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Floor plans</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {detailedFloorPlans.length === 0
                  ? 'No detailed floor plans uploaded for this configuration yet.'
                  : `${detailedFloorPlans.length} ${detailedFloorPlans.length === 1 ? 'file' : 'files'} available`}
              </p>
            </div>
            {detailedFloorPlans.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                <FileText size={12} />
                {detailedFloorPlans.length}
              </span>
            )}
          </div>

          {detailedFloorPlans.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 py-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <MapIcon className="text-slate-400" size={22} />
              </div>
              <p className="mt-3 text-base font-medium text-slate-800">No floor plans available</p>
              <p className="mt-1 text-sm text-slate-500">
                Detailed floor plans will appear here once uploaded.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {detailedFloorPlans.map((item, index) => {
                const docUrl = getMediaUrl(item);
                const docName =
                  item.name || item.key?.split('/').pop() || `Floor plan ${index + 1}`;
                const contentType = item.contentType || '';
                const isPDF = contentType.includes('pdf');
                const isDoc =
                  contentType.includes('word') ||
                  contentType.includes('document') ||
                  contentType.includes('msword');
                const FileIcon = isPDF ? FileText : isDoc ? File : FileText;
                return (
                  <a
                    key={index}
                    href={docUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-flamingo-200 hover:shadow-md"
                  >
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                        isPDF ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      <FileIcon size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">{docName}</p>
                      <p className="text-xs text-slate-500">
                        {isPDF ? 'PDF' : isDoc ? 'Document' : 'Open file'}
                      </p>
                    </div>
                    <ChevronRight size={14} className="text-slate-400 shrink-0" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BV_ConfigDetails;
